/**
 * Phase 6 (Feb 2026) — Platform-Operator area.
 *
 * Two routes:
 *   /app/operator                    → landing with tiles
 *   /app/operator/tenant-approvals   → pending-registration queue
 *
 * Both are role-gated on the client side (platform_owner / operator).
 * The backend enforces the same via ``require_platform_owner`` — this
 * client gate is UX only; a wrong role gets a 403 from the API.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, auth } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";

function useOperatorGuard() {
  const nav = useNavigate();
  const user = auth.getUser() || {};
  useEffect(() => {
    if (!user || (user.role !== "platform_owner" && user.role !== "operator")) {
      nav("/app/home", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return user;
}

export function OperatorLanding() {
  useOperatorGuard();
  const [counts, setCounts] = useState({ pending: 0 });
  useEffect(() => {
    api.get("/operator/tenant-approvals?state=pending&limit=1")
       .then((r) => setCounts(r.data?.counts || { pending: 0 }))
       .catch(() => {});
  }, []);
  return (
    <div className="space-y-6" data-testid="operator-landing">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">
          Plattform-Betreiber
        </h1>
        <p className="text-sm text-muted-foreground">
          Verwalte tenantübergreifende Aufgaben.
        </p>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          data-testid="operator-tile-tenant-approvals"
          className="hover:border-primary transition-colors"
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Tenant-Freigabe-Anträge</CardTitle>
            <Badge
              variant={counts.pending > 0 ? "destructive" : "secondary"}
              data-testid="operator-tile-pending-badge"
            >
              {counts.pending} offen
            </Badge>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Neue Registrierungen prüfen und freigeben oder ablehnen.
            </p>
            <Button asChild size="sm">
              <Link
                to="/app/operator/tenant-approvals"
                data-testid="operator-tile-open-approvals"
              >
                Warteliste öffnen
              </Link>
            </Button>
          </CardContent>
        </Card>
        <Card data-testid="operator-tile-budget-frames" className="opacity-60">
          <CardHeader><CardTitle className="text-base">Budget-Frames</CardTitle></CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Wird später hier verankert.
          </CardContent>
        </Card>
        <Card data-testid="operator-tile-feature-flags" className="opacity-60">
          <CardHeader><CardTitle className="text-base">Feature-Flags</CardTitle></CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Wird später hier verankert.
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function OperatorTenantApprovals() {
  useOperatorGuard();
  const [state, setState] = useState("pending");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});

  const load = async () => {
    try {
      const r = await api.get(
        `/operator/tenant-approvals?state=${state}&limit=100`);
      setRows(r.data?.items || []);
      setCounts(r.data?.counts || counts);
    } catch (e) {
      toast.error("Konnte Warteliste nicht laden.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [state]);

  const decide = async (tid, decision) => {
    setBusy(`${tid}:${decision}`);
    try {
      await api.post(
        `/operator/tenant-approvals/${tid}/${decision}`,
        { note: notes[tid] || "" });
      toast.success(decision === "approve" ? "Freigegeben" : "Abgelehnt");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(
        (d && (d.error || d.message)) || "Aktion fehlgeschlagen");
    } finally { setBusy(null); }
  };

  const filterTabs = useMemo(() => ([
    { k: "pending", label: `Offen (${counts.pending})` },
    { k: "approved", label: `Freigegeben (${counts.approved})` },
    { k: "rejected", label: `Abgelehnt (${counts.rejected})` },
    { k: "all", label: "Alle" },
  ]), [counts]);

  return (
    <div className="space-y-6" data-testid="operator-tenant-approvals">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">
          Tenant-Freigabe-Anträge
        </h1>
        <p className="text-sm text-muted-foreground">
          Neuregistrierungen laufen zuerst hier ein. Freigabe schaltet
          Tenant + Owner-User auf <code>active</code>; Ablehnung setzt
          beide auf <code>rejected</code>.
        </p>
      </header>
      <div className="flex flex-wrap gap-2">
        {filterTabs.map((f) => (
          <Button
            key={f.k}
            size="sm"
            variant={state === f.k ? "default" : "outline"}
            onClick={() => setState(f.k)}
            data-testid={`operator-approvals-filter-${f.k}`}
          >
            {f.label}
          </Button>
        ))}
      </div>
      <Card>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Firma / Slug</TableHead>
                <TableHead>Kontakt</TableHead>
                <TableHead>Eingegangen</TableHead>
                <TableHead>Kontext</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow data-testid="operator-approvals-empty">
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                    Keine Einträge in dieser Ansicht.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow
                  key={r.id}
                  data-testid={`operator-approval-row-${r.tenant_slug}`}
                >
                  <TableCell>
                    <div className="text-sm font-medium">{r.tenant_name}</div>
                    <code className="text-xs font-mono text-muted-foreground">
                      {r.tenant_slug}
                    </code>
                  </TableCell>
                  <TableCell className="text-xs">{r.owner_email}</TableCell>
                  <TableCell className="text-xs">
                    {new Date(r.requested_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs max-w-[240px] whitespace-normal">
                    {r.context || "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={
                      r.state === "pending" ? "default"
                        : r.state === "approved" ? "secondary" : "destructive"
                    }>
                      {r.state}
                    </Badge>
                  </TableCell>
                  <TableCell className="min-w-[220px]">
                    {r.state === "pending" ? (
                      <div className="space-y-2">
                        <Textarea
                          rows={2}
                          placeholder="Notiz (optional)"
                          value={notes[r.tenant_id] || ""}
                          onChange={(e) => setNotes((s) => ({ ...s, [r.tenant_id]: e.target.value }))}
                          data-testid={`operator-approval-note-${r.tenant_slug}`}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            disabled={busy === `${r.tenant_id}:approve`}
                            onClick={() => decide(r.tenant_id, "approve")}
                            data-testid={`operator-approve-${r.tenant_slug}`}
                          >
                            Freigeben
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={busy === `${r.tenant_id}:reject`}
                            onClick={() => decide(r.tenant_id, "reject")}
                            data-testid={`operator-reject-${r.tenant_slug}`}
                          >
                            Ablehnen
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {r.decided_at ? new Date(r.decided_at).toLocaleString() : "—"}
                        {r.decision_note && (
                          <div className="mt-1 italic">„{r.decision_note}"</div>
                        )}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
